import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
  useTheme,
} from "@mui/material";
import {
  addMonths,
  eachDayOfInterval,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  getDay,
  isSameDay,
  isSameMonth,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subMonths,
} from "date-fns";

import Header from "../../components/Header";
import ActionButton from "../../components/ActionButton";
import CreateHolidayRequestModal from "../../components/CreateHolidayRequestModal";
import { tokens } from "../../theme/tokens";
import { useResolvedTenantId } from "../../hooks/useResolvedTenantId";
import { useAuth } from "../../auth/AuthContext";
import useGraphQLClient from "../../hooks/useGraphQLClient";

import {
  READ_PUBLIC_HOLIDAYS,
  READ_TENANT_USERS_BY_TENANT,
  READ_TENANT_HOLIDAY_SETTINGS,
  READ_STAFF_WORK_PATTERNS,
  READ_HOLIDAY_REQUESTS,
} from "../../graph/holidayManagement/queries.js";
import { READ_STAFF_CALENDAR_EVENTS } from "../../graph/tasks/queries";

const WEEK_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const formatMinutes = (minutes = 0) => {
  if (!minutes) return "0m";

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (!hours) return `${remainingMinutes}m`;
  if (!remainingMinutes) return `${hours}h`;

  return `${hours}h ${remainingMinutes}m`;
};

const StaffCalendar = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const client = useGraphQLClient();
  const { tenantId, tenantName } = useResolvedTenantId();
  const { user } = useAuth();

  const eventColors = {
    work: colors.scalpelTeal[400],
    staffEvent: "#4F83CC",
    publicHoliday: "#9B7ED8",
    requested: "#D9A441",
    granted: "#55A878",
  };

  const [holidayRequestOpen, setHolidayRequestOpen] = useState(false);
  const [visibleMonth, setVisibleMonth] = useState(new Date());
  const [staffTenantUserID, setStaffTenantUserID] = useState("");
  const [leaveYears, setLeaveYears] = useState([]);
  const [leaveYearID, setLeaveYearID] = useState("");
  const [jurisdiction, setJurisdiction] = useState("");
  const [workPattern, setWorkPattern] = useState(null);
  const [holidayRequests, setHolidayRequests] = useState([]);
  const [publicHolidays, setPublicHolidays] = useState([]);
  const [staffCalendarTasks, setStaffCalendarTasks] = useState([]);
  const [loadingStaff, setLoadingStaff] = useState(true);
  const [loadingCalendar, setLoadingCalendar] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const monthStart = useMemo(
    () => startOfMonth(visibleMonth),
    [visibleMonth]
  );

  const monthEnd = useMemo(
    () => endOfMonth(visibleMonth),
    [visibleMonth]
  );

  const calendarStart = useMemo(
    () => startOfWeek(monthStart, { weekStartsOn: 1 }),
    [monthStart]
  );

  const calendarEnd = useMemo(
    () => endOfWeek(monthEnd, { weekStartsOn: 1 }),
    [monthEnd]
  );

  const calendarDays = useMemo(
    () =>
      eachDayOfInterval({
        start: calendarStart,
        end: calendarEnd,
      }),
    [calendarStart, calendarEnd]
  );

  const currentUserID = user?.id || user?.userId || "";

  useEffect(() => {
    const fetchStaffDetails = async () => {
      if (!tenantId || !currentUserID) {
        setStaffTenantUserID("");
        setLeaveYears([]);
        setLeaveYearID("");
        setJurisdiction("");
        setLoadingStaff(false);
        return;
      }

      setLoadingStaff(true);
      setErrorMessage("");

      try {
        const [usersData, settingsData] = await Promise.all([
          client.request(READ_TENANT_USERS_BY_TENANT, { tenantId }),
          client.request(READ_TENANT_HOLIDAY_SETTINGS, {
            tenantID: tenantId,
          }),
        ]);

        const tenantUser = (
          usersData.readTenantUsersByTenant || []
        ).find(
          (membership) =>
            !membership.archivedAt &&
            (membership.userId === currentUserID ||
              membership.user?.id === currentUserID)
        );

        if (!tenantUser) {
          setStaffTenantUserID("");
          setErrorMessage(
            "Your staff membership could not be found for this tenant."
          );
          return;
        }

        const settings = settingsData.readTenantHolidaySettings;
        const years = settings?.leaveYears || [];
        const today = format(new Date(), "yyyy-MM-dd");

        const activeYear = years.find(
          (year) => year.startDate <= today && today < year.endDate
        );
        const openYear = years.find(
          (year) => year.status === "OPEN"
        );
        const selectedYear = activeYear || openYear || years[0];

        setStaffTenantUserID(tenantUser.id);
        setLeaveYears(years);
        setJurisdiction(
          settings?.publicHolidayJurisdiction || ""
        );
        setLeaveYearID(selectedYear?.id || "");

        if (
          selectedYear &&
          !(selectedYear.startDate <= today && today < selectedYear.endDate)
        ) {
          setVisibleMonth(parseISO(selectedYear.startDate));
        }
      } catch (err) {
        console.error("Failed to load staff calendar details:", err);
        setErrorMessage("Failed to load your staff calendar details.");
      } finally {
        setLoadingStaff(false);
      }
    };

    fetchStaffDetails();
  }, [client, tenantId, currentUserID]);

  const fetchCalendarData = useCallback(async () => {
    if (!tenantId || !staffTenantUserID || !leaveYearID || !jurisdiction) {
      setWorkPattern(null);
      setHolidayRequests([]);
      setPublicHolidays([]);
      setStaffCalendarTasks([]);
      return;
    }

    setLoadingCalendar(true);
    setErrorMessage("");

    try {
      const years = [
        ...new Set(
          calendarDays.map((day) => Number(format(day, "yyyy")))
        ),
      ];

      const publicHolidayRequests = years.map((year) =>
        client.request(READ_PUBLIC_HOLIDAYS, {
          jurisdiction,
          year,
        })
      );

      const [
        patternsData,
        requestsData,
        tasksData,
        ...publicHolidayData
      ] = await Promise.all([
        client.request(READ_STAFF_WORK_PATTERNS, {
          tenantID: tenantId,
          staffTenantUserID,
          leaveYearID,
        }),
        client.request(READ_HOLIDAY_REQUESTS, {
          filter: {
            tenantID: tenantId,
            staffTenantUserID,
            leaveYearID,
            fromDate: format(calendarStart, "yyyy-MM-dd"),
            toDate: format(calendarEnd, "yyyy-MM-dd"),
          },
        }),
        client.request(READ_STAFF_CALENDAR_EVENTS, {
          tenantId,
          assignedTenantUserId: staffTenantUserID,
          dueFrom: startOfDay(calendarStart).toISOString(),
          dueTo: endOfDay(calendarEnd).toISOString(),
        }),
        ...publicHolidayRequests,
      ]);

      const patterns = patternsData.readStaffWorkPatterns || [];
      setWorkPattern(patterns[0] || null);

      setHolidayRequests(
        (requestsData.readHolidayRequests || []).filter((request) =>
          ["PENDING", "APPROVED"].includes(request.status)
        )
      );

      setStaffCalendarTasks(
        (tasksData.readAllTasks || []).filter((task) =>
          task.labels?.includes("staff-calendar-event")
        )
      );

      setPublicHolidays(
        publicHolidayData.flatMap(
          (result) => result.readPublicHolidays || []
        )
      );
    } catch (err) {
      console.error("Failed to load staff calendar:", err);
      setErrorMessage("Failed to load calendar details.");
      setWorkPattern(null);
      setHolidayRequests([]);
      setPublicHolidays([]);
      setStaffCalendarTasks([]);
    } finally {
      setLoadingCalendar(false);
    }
  }, [
    client,
    tenantId,
    staffTenantUserID,
    leaveYearID,
    jurisdiction,
    calendarDays,
    calendarStart,
    calendarEnd,
  ]);

  useEffect(() => {
    fetchCalendarData();
  }, [fetchCalendarData]);

  const handleLeaveYearChange = (event) => {
    const selectedID = event.target.value;
    setLeaveYearID(selectedID);

    const selectedYear = leaveYears.find(
      (year) => year.id === selectedID
    );

    if (selectedYear) {
      setVisibleMonth(parseISO(selectedYear.startDate));
    }
  };

  const getEventsForDay = (day) => {
    const date = format(day, "yyyy-MM-dd");
    const events = [];

    const selectedYear = leaveYears.find(
      (year) => year.id === leaveYearID
    );

    const patternIsEffective =
      workPattern &&
      date >= workPattern.effectiveFrom &&
      (!workPattern.effectiveTo || date <= workPattern.effectiveTo) &&
      (!selectedYear ||
        (date >= selectedYear.startDate && date < selectedYear.endDate));

    if (patternIsEffective) {
      const dayName = format(day, "EEEE").toUpperCase();
      const patternDay = workPattern.days?.find(
        (item) => item.dayOfWeek === dayName
      );

      if (patternDay?.isWorkingDay) {
        events.push({
          key: `work-${dayName}`,
          label: `Work ${patternDay.startTime || ""}–${patternDay.endTime || ""}`,
          color: eventColors.work,
        });
      }
    }

    publicHolidays.forEach((holiday) => {
      const isActualDate = holiday.date === date;
      const isObservedDate =
        holiday.observedDate && holiday.observedDate === date;

      if (isActualDate || isObservedDate) {
        events.push({
          key: `public-holiday-${holiday.id}-${date}`,
          label:
            isObservedDate && !isActualDate
              ? `${holiday.name} (observed)`
              : holiday.name,
          color: eventColors.publicHoliday,
        });
      }
    });

    holidayRequests.forEach((request) => {
      const requestDay = (request.days || []).find(
        (item) => item.date === date
      );

      if (!requestDay) return;

      events.push({
        key: `leave-${request.id}-${date}`,
        label: `${
          request.status === "APPROVED"
            ? "Holiday granted"
            : "Holiday requested"
        } · ${formatMinutes(requestDay.chargedMinutes)}`,
        color:
          request.status === "APPROVED"
            ? eventColors.granted
            : eventColors.requested,
      });
    });

    staffCalendarTasks.forEach((task) => {
      if (!task.dueDate) return;

      const taskDate = format(parseISO(task.dueDate), "yyyy-MM-dd");
      if (taskDate !== date) return;

      events.push({
        key: `staff-calendar-task-${task.id}`,
        label: task.title,
        color: eventColors.staffEvent,
      });
    });

    return events;
  };

  const legendItems = [
    { label: "Work schedule", color: eventColors.work },
    { label: "Work event", color: eventColors.staffEvent },
    { label: "Public holiday", color: eventColors.publicHoliday },
    { label: "Holiday requested", color: eventColors.requested },
    { label: "Holiday granted", color: eventColors.granted },
  ];

  if (!tenantId) {
    return (
      <Box>
        <Header
          title="MY WORK CALENDAR"
          subtitle="Your tenant calendar is not available"
        />
        <Typography mt={3}>
          Sign in with a tenant staff account to view your calendar.
        </Typography>
      </Box>
    );
  }

  if (loadingStaff) {
    return (
      <Box display="flex" alignItems="center" gap={1} mt={3}>
        <CircularProgress size={20} />
        <Typography>Loading your calendar...</Typography>
      </Box>
    );
  }

  if (errorMessage && !staffTenantUserID) {
    return (
      <Box>
        <Header
          title="MY WORK CALENDAR"
          subtitle={
            tenantName
              ? `Calendar for ${tenantName}`
              : "Staff calendar"
          }
        />
        <Typography mt={3}>{errorMessage}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
        flexWrap="wrap"
        mb={2}
      >
        <Header
          title="MY WORK CALENDAR"
          subtitle={
            tenantName
              ? `Calendar for ${tenantName}`
              : "Your work calendar"
          }
        />

        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="staff-calendar-year-label">
              Leave year
            </InputLabel>
            <Select
              labelId="staff-calendar-year-label"
              value={leaveYearID}
              label="Leave year"
              onChange={handleLeaveYearChange}
            >
              {leaveYears.map((year) => (
                <MenuItem key={year.id} value={year.id}>
                  {year.startDate} to {year.endDate}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <ActionButton
            tone="teal"
            variant="outlined"
            onClick={() =>
              setVisibleMonth((current) => subMonths(current, 1))
            }
          >
            Previous
          </ActionButton>

          <ActionButton
            tone="teal"
            variant="outlined"
            onClick={() => {
              const selectedYear = leaveYears.find(
                (year) => year.id === leaveYearID
              );
              const today = new Date();
              const todayKey = format(today, "yyyy-MM-dd");

              if (
                selectedYear &&
                (todayKey < selectedYear.startDate ||
                  todayKey >= selectedYear.endDate)
              ) {
                setVisibleMonth(parseISO(selectedYear.startDate));
              } else {
                setVisibleMonth(today);
              }
            }}
          >
            Today
          </ActionButton>

          <ActionButton
            tone="teal"
            variant="outlined"
            onClick={() =>
              setVisibleMonth((current) => addMonths(current, 1))
            }
          >
            Next
          </ActionButton>

          <ActionButton
            tone="gold"
            onClick={() => setHolidayRequestOpen(true)}
            disabled={!staffTenantUserID || !leaveYearID || !workPattern}
          >
            Request Holiday
          </ActionButton>
        </Box>
      </Box>

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        gap={2}
        flexWrap="wrap"
      >
        <Typography variant="h4" fontWeight={700}>
          {format(visibleMonth, "MMMM yyyy")}
        </Typography>

        {loadingCalendar && (
          <Box display="flex" alignItems="center" gap={1}>
            <CircularProgress size={18} />
            <Typography variant="body2">Loading calendar...</Typography>
          </Box>
        )}
      </Box>

      {errorMessage && (
        <Typography color="error" mb={2}>
          {errorMessage}
        </Typography>
      )}

      <Paper
        variant="outlined"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexWrap: "wrap",
          width: "fit-content",
          maxWidth: "100%",
          px: 2,
          py: 1,
          mb: 2,
          borderRadius: 99,
        }}
      >
        {legendItems.map((item, index) => (
          <React.Fragment key={item.label}>
            {index > 0 && (
              <Typography variant="body2" color="text.secondary">
                ·
              </Typography>
            )}

            <Box display="flex" alignItems="center" gap={0.75}>
              <Box
                sx={{
                  width: 8,
                  height: 18,
                  borderRadius: 1,
                  bgcolor: item.color,
                  flexShrink: 0,
                }}
              />
              <Typography variant="body2">{item.label}</Typography>
            </Box>
          </React.Fragment>
        ))}
      </Paper>

      <Box
        display="grid"
        gridTemplateColumns="repeat(7, minmax(0, 1fr))"
        gap={1}
        mb={1}
      >
        {WEEK_DAYS.map((day) => (
          <Typography
            key={day}
            align="center"
            variant="body2"
            fontWeight={700}
            sx={{ color: colors.grey[400] }}
          >
            {day}
          </Typography>
        ))}
      </Box>

      <Box
        display="grid"
        gridTemplateColumns="repeat(7, minmax(0, 1fr))"
        gap={1}
      >
        {calendarDays.map((day) => {
          const events = getEventsForDay(day);
          const isToday = isSameDay(day, new Date());
          const isCurrentMonth = isSameMonth(day, visibleMonth);
          const isWeekend = [0, 6].includes(getDay(day));

          return (
            <Paper
              key={format(day, "yyyy-MM-dd")}
              elevation={isToday ? 4 : 1}
              sx={{
                p: 1,
                minHeight: 150,
                display: "flex",
                flexDirection: "column",
                gap: 0.75,
                cursor: "default",
                border: isToday
                  ? `2px solid ${colors.scalpelTeal[400]}`
                  : `1px solid ${colors.surface.border}`,
                bgcolor: "background.paper",
                opacity: isCurrentMonth ? 1 : 0.45,
              }}
            >
              <Typography
                variant="body2"
                fontWeight={isToday ? 800 : 700}
                sx={{
                  color: isToday
                    ? colors.scalpelTeal[300]
                    : isWeekend
                    ? colors.grey[400]
                    : "text.primary",
                }}
              >
                {format(day, "d")}
              </Typography>

              {events.length > 0 && (
                <Box display="flex" flexDirection="column" gap={0.75}>
                  {events.map((event) => (
                    <Paper
                      key={event.key}
                      elevation={1}
                      title={event.label}
                      sx={{
                        p: 0.75,
                        borderRadius: 1,
                        bgcolor: event.color,
                        color: "#111111",
                        border: "1px solid rgba(0, 0, 0, 0.18)",
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="inherit"
                        fontWeight={700}
                        sx={{
                          display: "block",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {event.label}
                      </Typography>
                    </Paper>
                  ))}
                </Box>
              )}
            </Paper>
          );
        })}
      </Box>

      <CreateHolidayRequestModal
        open={holidayRequestOpen}
        onClose={() => setHolidayRequestOpen(false)}
        onCreated={fetchCalendarData}
        tenantID={tenantId}
        staffTenantUserID={staffTenantUserID}
        leaveYear={leaveYears.find((year) => year.id === leaveYearID)}
        workPattern={workPattern}
      />
    </Box>
  );
};

export default StaffCalendar;